import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { GAMES, getGame } from "@/content/games";
import { DIFFICULTIES } from "@/content/categories";
import { recordGameScore, getGameBest } from "@/lib/progress";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import PageHero from "@/components/shared/PageHero";
import AlphabetMatch from "@/components/games/AlphabetMatch";
import NumberChallenge from "@/components/games/NumberChallenge";
import ShapeColorQuiz from "@/components/games/ShapeColorQuiz";
import WordBuilder from "@/components/games/WordBuilder";
import KnowledgeQuiz from "@/components/games/KnowledgeQuiz";
import MemoryMatch from "@/components/games/MemoryMatch";

const REGISTRY = {
  "alphabet-match": AlphabetMatch,
  "number-challenge": NumberChallenge,
  "shape-color-quiz": ShapeColorQuiz,
  "word-builder": WordBuilder,
  "knowledge-quiz": KnowledgeQuiz,
  "memory-match": MemoryMatch,
};

export default function Play() {
  const { t, code } = useLang();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState(null);
  const [phase, setPhase] = useState("intro");
  const [difficulty, setDifficulty] = useState("easy");
  const [result, setResult] = useState(null);
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    const g = params.get("game");
    if (g && getGame(g)) {
      setSelected(g);
      setPhase("intro");
    }
  }, [params]);

  const openGame = (id) => {
    setSelected(id);
    setPhase("intro");
    setDifficulty("easy");
    setResult(null);
    setParams({ game: id });
  };
  const closeGame = () => {
    setSelected(null);
    setParams({});
  };
  const start = () => {
    setResult(null);
    setRunKey((k) => k + 1);
    setPhase("play");
  };
  const finish = (score, total) => {
    const best = recordGameScore(selected, score);
    setResult({ score, total, best });
    setPhase("summary");
  };

  const game = selected ? getGame(selected) : null;
  const GameComp = selected ? REGISTRY[selected] : null;

  return (
    <>
      <Seo title={t("play.title")} description={t("play.subtitle")} />
      <PageHero eyebrow={t("nav.play")} title={t("play.title")} subtitle={t("play.subtitle")} tone="yellow" />

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {!selected ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {GAMES.map((g, i) => (
              <motion.button
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                onClick={() => openGame(g.id)}
                data-testid={`game-card-${g.id}`}
                className="group flex flex-col rounded-3xl border-4 bg-white p-6 text-left transition-all hover:-translate-y-1"
                style={{ borderColor: `${g.color}66` }}
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: `${g.color}1f` }}>
                  <Icon name={g.icon} className="h-7 w-7" style={{ color: g.color }} />
                </span>
                <h3 className="font-display mt-4 text-xl font-bold text-[#082B66]">{g.title[code]}</h3>
                <p className="mt-2 flex-1 text-sm text-[#475569]">{g.desc[code]}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#64748B]">{t("common.yourBest")}: {getGameBest(g.id)}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB]">{t("play.start")} <Icon name="Play" className="h-4 w-4" /></span>
                </div>
              </motion.button>
            ))}
          </div>
        ) : (
          <div className="mx-auto max-w-2xl">
            <button onClick={closeGame} data-testid="game-back-button" className="mb-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB] hover:underline">
              <Icon name="ArrowLeft" className="h-4 w-4" /> {t("nav.play")}
            </button>

            <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-6 card-shadow-soft sm:p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: `${game.color}1f` }}>
                  <Icon name={game.icon} className="h-6 w-6" style={{ color: game.color }} />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-bold text-[#082B66]">{game.title[code]}</h2>
                  <p className="text-sm text-[#64748B]">{game.desc[code]}</p>
                </div>
              </div>

              <div className="mt-6">
                {phase === "intro" && (
                  <div>
                    {game.hasDifficulty && (
                      <div>
                        <p className="text-sm font-bold text-[#475569]">{t("play.chooseDifficulty")}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {DIFFICULTIES.map((d) => (
                            <button
                              key={d.id}
                              onClick={() => setDifficulty(d.id)}
                              data-testid={`difficulty-${d.id}`}
                              aria-pressed={difficulty === d.id}
                              className={`rounded-2xl border-2 px-5 py-2.5 text-sm font-bold transition-colors ${
                                difficulty === d.id ? "border-[#147DEB] bg-[#147DEB] text-white" : "border-[#147DEB]/15 bg-white text-[#082B66] hover:border-[#147DEB]/50"
                              }`}
                            >
                              {t(d.key)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <button onClick={start} data-testid="game-start-button" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FFC928] px-6 py-3.5 font-extrabold text-[#082B66] shadow-lg shadow-[#FFC928]/30 transition-all hover:-translate-y-0.5">
                      <Icon name="Play" className="h-5 w-5" /> {t("play.start")}
                    </button>
                  </div>
                )}

                {phase === "play" && GameComp && (
                  <GameComp key={runKey} difficulty={difficulty} onFinish={finish} />
                )}

                {phase === "summary" && result && (
                  <div className="text-center" data-testid="game-summary">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFC928]/20 ring-4 ring-[#FFC928]/30">
                      <Icon name="Trophy" className="h-8 w-8 text-[#FFC928]" />
                    </span>
                    <h3 className="font-display mt-4 text-2xl font-extrabold text-[#082B66]">{t("play.summary")}</h3>
                    <p className="mt-2 text-lg font-bold text-[#147DEB]" data-testid="game-final-score">
                      {t("play.finalScore")}: {result.score} / {result.total}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-[#64748B]">{t("common.yourBest")}: {result.best}</p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                      <button onClick={start} data-testid="game-restart-button" className="inline-flex items-center gap-2 rounded-2xl bg-[#147DEB] px-6 py-3 font-bold text-white hover:bg-[#0F62BD]">
                        <Icon name="RotateCcw" className="h-4 w-4" /> {t("common.restart")}
                      </button>
                      <button onClick={closeGame} className="inline-flex items-center gap-2 rounded-2xl bg-[#E8F5FF] px-6 py-3 font-bold text-[#147DEB]">
                        {t("nav.play")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
